// Ediciones del borrador (JSON pointer + antes/después) y cómo se le enseñan a una persona.
import { leerRuta, numero, pesos } from '@cotizador/compartido';
import type { ContenidoCotizacion, EdicionCampo } from '@cotizador/compartido';
import { ETIQUETA_OPCION } from './etiquetas.ts';

export function reemplazo(ruta: string, antes: unknown, despues: unknown, motivo: string | null = null): EdicionCampo {
  return { op: 'reemplazar', ruta, antes: antes === undefined ? null : antes, despues, motivo };
}

const CAMPOS: Record<string, string> = {
  consumo_kwh: 'consumo (kWh)',
  importe: 'importe',
  demanda_kw: 'demanda (kW)',
  precio_medio: 'precio medio',
  kwh_base: 'kWh base',
  kwh_intermedia: 'kWh intermedia',
  kwh_punta: 'kWh punta',
  etiqueta: 'periodo',
  inicio: 'inicio',
  fin: 'fin',
  origen: 'origen',
  sugerido_ia: 'estimado por la IA',
  tarifa: 'tarifa',
  facturacion: 'facturación',
  total_a_pagar: 'total a pagar',
  demanda_contratada_kw: 'demanda contratada (kW)',
  carga_conectada_kw: 'carga conectada (kW)',
  multiplicador: 'multiplicador',
  titular: 'titular',
  numero_servicio: 'número de servicio',
  direccion: 'dirección',
  rmu: 'RMU',
  cuenta: 'cuenta',
  numero_medidor: 'número de medidor',
  historial: 'historial',
  recomendada: 'opción recomendada',
  iva_pct: 'IVA',
  ajustes: 'ajuste manual',
  opciones_activas: 'opciones activas',
  finsus: 'FINSUS',
  panel: 'panel',
  generacion: 'generación por kW de la obra',
  concepto: 'concepto',
  cantidad: 'cantidad',
  precio_cliente_sin_iva: 'precio al cliente',
  costo_solarix_sin_iva: 'costo para Solarix',
  aplica_descuento_contado: 'descuento de contado',
  reportado_por: 'reportado por',
  nota: 'nota',
};

/** Ampliación de baterías (27-oct-2026): cada dato de su captura (/piezas/…) como lo dice el vendedor. El historial y los
 *  errores enseñaban «/piezas/costo_bateria» (verificación de ventas del 27-oct). */
const PIEZAS: Readonly<Record<string, string>> = {
  '': 'toda la captura de la ampliación',
  bateria: 'batería',
  actuales: 'baterías que ya tiene',
  nuevas: 'baterías que se suman',
  costo_bateria: 'costo de la batería',
  'costo_bateria/sin_iva': 'costo de la batería',
  'costo_bateria/fuente': 'de dónde sale el costo de la batería',
  costo_material_sin_iva: 'material',
  costo_instalacion_sin_iva: 'instalación',
  costo_viaticos_sin_iva: 'viáticos',
  precio_sujeto_a_confirmacion: 'precio sujeto a confirmación del proveedor',
  anticipo_pct: 'anticipo',
  entrega_dias_habiles: 'días hábiles de entrega',
  'entrega_dias_habiles/min': 'entrega desde (días hábiles)',
  'entrega_dias_habiles/max': 'entrega hasta (días hábiles)',
  hoja2: 'hoja 2',
  'hoja2/corte_pct': 'hoja 2: corte del inversor',
  'hoja2/reserva_fabricante_pct': 'hoja 2: reserva del fabricante',
  'hoja2/noche': 'hoja 2: la noche medida',
  'hoja2/noche/fecha': 'hoja 2: fecha de la noche',
  'hoja2/noche/como': 'hoja 2: qué estaba prendido',
  'hoja2/noche/inicio': 'hoja 2: hora en que empezó a bajar',
  'hoja2/noche/inicio_pct': 'hoja 2: carga al empezar la noche',
  'hoja2/noche/corte': 'hoja 2: hora del corte',
  'hoja2/noche/sol': 'hoja 2: hora en que los paneles cubren la casa',
  'hoja2/noche_normal_max_pct': 'hoja 2: lo que usa una noche normal',
  'hoja2/recarga': 'hoja 2: la recarga medida',
  'hoja2/recarga/fecha': 'hoja 2: fecha de la recarga',
  'hoja2/recarga/llenas_a_las': 'hoja 2: hora en que se llenaron',
  'hoja2/recarga/carga_kw': 'hoja 2: kW con que cargaban',
  'hoja2/paneles': 'hoja 2: paneles que ya tiene',
  'hoja2/paneles/cantidad': 'hoja 2: cuántos paneles tiene',
  'hoja2/paneles/w': 'hoja 2: watts de cada panel',
  'hoja2/fuente': 'hoja 2: de dónde salen las horas',
};

/** «/piezas/hoja2/noche/corte» → «hoja 2: hora del corte»; null si no es una ruta de la ampliación que se conozca. */
export function nombrePieza(ruta: string): string | null {
  if (ruta !== '/piezas' && !ruta.startsWith('/piezas/')) return null;
  return PIEZAS[ruta.slice('/piezas'.length).replace(/^\//, '')] ?? null;
}

/** Un texto del servidor con rutas de la ampliación («Cambió 2 datos: /piezas/costo_bateria, /piezas/hoja2/noche/corte.») con
 *  sus nombres («costo de la batería, hoja 2: hora del corte»). Lo demás queda igual. */
export function detalleLegible(texto: string): string {
  return texto.replace(/\/piezas(?:\/[a-z0-9_]+)*/g, (r) => nombrePieza(r) ?? r);
}

/** "/lecturas/A/historial/6/consumo_kwh" → "Medidor A · JUL 26 · consumo (kWh)". */
export function etiquetaRuta(ruta: string, contenido: ContenidoCotizacion | null): string {
  const pieza = nombrePieza(ruta);
  if (pieza) return `Ampliación · ${pieza}`;
  if (ruta === '/parametros/generacion/municipio') return 'Municipio de la obra';
  const p = ruta.split('/').filter(Boolean);
  if (p[0] === 'lecturas') {
    const m = `Medidor ${p[1]}`;
    if (p[2] === 'historial' && p[3] !== undefined) {
      const periodo = contenido ? (leerRuta(contenido, `/lecturas/${p[1]}/historial/${p[3]}/etiqueta`) as string | undefined) : undefined;
      const nombre = periodo ?? (p[3] === '-' ? 'periodo nuevo' : `periodo ${Number(p[3]) + 1}`);
      return p[4] ? `${m} · ${nombre} · ${CAMPOS[p[4]] ?? p[4]}` : `${m} · ${nombre}`;
    }
    if (p[2] === 'privados') return `${m} · dato privado · ${CAMPOS[p[3] ?? ''] ?? p[3]}`;
    if (!p[2]) return `${m} · lectura completa`;
    return `${m} · ${CAMPOS[p[2]] ?? p[2]}`;
  }
  if (p[0] === 'parametros') {
    if (p[1] === 'ajustes') return 'Ajuste manual de una opción';
    if (p[1] === 'formas_de_pago') return `Formas de pago · ${p[2] ?? ''}`.trim();
    return `Cálculo · ${CAMPOS[p[1] ?? ''] ?? p[1]}`;
  }
  if (p[0] === 'cuestionario') return p.length === 1 ? 'Cuestionario completo' : `Cuestionario · ${p.slice(1).join(' · ')}`;
  if (p[0] === 'costos_adicionales') return `Costo adicional · ${CAMPOS[p[2] ?? ''] ?? p[2]}`;
  return ruta;
}

/** Un valor de la captura de la ampliación que es un objeto (la batería, el costo, la entrega, la recarga, los paneles…). */
function valorPieza(v: Record<string, unknown>, ruta: string): string | null {
  const campo = ruta.slice('/piezas'.length).replace(/^\//, '');
  if (campo === 'bateria' && typeof v.marca === 'string' && typeof v.modelo === 'string') return `${v.marca} ${v.modelo}`;
  if (campo === 'costo_bateria') {
    if (v.origen === 'tabla') return 'el de la tabla de precios';
    if (v.origen === 'proveedor' && typeof v.sin_iva === 'number') return `${pesos(v.sin_iva)} del proveedor${typeof v.para === 'string' ? ` (${v.para})` : ''}`;
  }
  if (campo === 'entrega_dias_habiles' && typeof v.min === 'number' && typeof v.max === 'number') return `de ${v.min} a ${v.max} días hábiles`;
  if (campo === 'hoja2/recarga' && typeof v.llenas_a_las === 'string') return `llenas a las ${v.llenas_a_las}${typeof v.fecha === 'string' ? ` (${v.fecha})` : ''}`;
  if (campo === 'hoja2/paneles' && typeof v.cantidad === 'number' && typeof v.w === 'number') return `${v.cantidad} de ${v.w} W`;
  if (campo === 'hoja2' || campo === 'hoja2/noche') return 'capturada';
  if (campo === '') return 'la captura completa';
  return null;
}

/** Valor corto para enseñar en un "antes → después". */
export function valorCorto(v: unknown, ruta = ''): string {
  if (v === null || v === undefined) return 'vacío';
  if (typeof v === 'number' && /^\/piezas\/.*_pct$/.test(ruta)) return `${numero(v, Number.isInteger(v) ? 0 : 1)} %`;
  if (v && typeof v === 'object' && !Array.isArray(v) && (ruta === '/piezas' || ruta.startsWith('/piezas/'))) {
    const t = valorPieza(v as Record<string, unknown>, ruta);
    if (t) return t;
  }
  if (typeof v === 'number') {
    if (/importe|precio|total|costo/.test(ruta)) return pesos(v);
    return numero(v, Number.isInteger(v) ? 0 : 2);
  }
  if (typeof v === 'boolean') return v ? 'sí' : 'no';
  // Etapa 3: la generación de la obra ("1,670 kWh/kWp al año" o "la base de Diego")
  if (/\/generacion$/.test(ruta) && typeof v === 'object' && !Array.isArray(v)) {
    const k = (v as { kwh_kwp?: number | null }).kwh_kwp;
    return typeof k === 'number' ? `${numero(k)} kWh por kWp al año` : 'la base de Diego';
  }
  if (typeof v === 'string') return ETIQUETA_OPCION[v as keyof typeof ETIQUETA_OPCION] ?? v;
  if (Array.isArray(v)) return `${v.length} ${v.length === 1 ? 'elemento' : 'elementos'}`;
  return 'cambio de varios datos';
}
